import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// ► Session-Bridging sicherstellen (SSO → Mentoring)
import ensureMentoringSession from '../utils/ensureMentoringSession';
import { usePairing } from '../context/PairingContext';

const locales = { de };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

function MenteeCalendar({ user, mentor, reloadTrigger }) {
  const { selectedMatchingId } = usePairing();
  const { t, i18n } = useTranslation();

  const [events, setEvents] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // ► Zeitzone des Browsers ermitteln
  const [localTimeZone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );

  // Defaults abhängig von UI-Sprache (falls Translation-Key fehlt)
  const uiLang = String(i18n?.language || 'de').toLowerCase();
  const defaultTzLabel = uiLang.startsWith('de')
    ? `Deine Zeitzone: ${localTimeZone}`
    : `Your time zone: ${localTimeZone}`;

  const defaultTzTooltip = uiLang.startsWith('de')
    ? 'Wenn dein Mentoringpartner in einer anderen Zeitzone ist, stimmt euch bitte zur Uhrzeit ab.'
    : 'If your mentoring partner is in a different time zone, please coordinate the time accordingly.';

  const defaultTzNotice = uiLang.startsWith('de')
    ? `Alle Zeiten beziehen sich auf deine lokale Zeitzone (${localTimeZone}).`
    : `All times refer to your local time zone (${localTimeZone}).`;

  // Logging helper
  const log = (...args) => {
    if (process.env.NODE_ENV !== 'production') console.log('[MenteeCalendar]', ...args);
  };

  const fetchEvents = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      // ► vor dem ersten Call Session am Mentoring-Backend sicherstellen
      await ensureMentoringSession();

      const res = await axios.get(`/mentoring/calendar${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`, { withCredentials: true });
      const loaded = res.data.map((item) => ({
        id: item.appointment_id,
        title: item.title,
        start: new Date(item.start_time),
        end: new Date(item.end_time),
        type: item.type,
      }));
      setEvents((prev) => {
        const prevJSON = JSON.stringify(prev);
        const nextJSON = JSON.stringify(loaded);
        return prevJSON !== nextJSON ? loaded : prev;
      });
      log('Events geladen:', loaded);
    } catch (err) {
      setErrorMsg(t('calendar.loadError'));
      log('Fehler beim Laden der Termine:', err);
    } finally {
      setLoading(false);
    }
  };

  // Nutze reloadTrigger (wenn du z.B. von außen ein globales Update triggern willst)
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line
  }, [reloadTrigger, selectedMatchingId]);

  const handleSelectEvent = (event) => {
    setModalData({ ...event, isEdit: true });
  };

  const handleSelectSlot = ({ start, end }) => {
    setModalData({ start, end, isEdit: false });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    const form = e.target;
    const type = form.type.value;
    const title = form.title.value;
    const start = new Date(form.start.value);
    const end = new Date(form.end.value);

    // Guard: matching_id muss gesetzt sein (Backend Pflichtfeld)
    if (!selectedMatchingId) {
      setErrorMsg(t('calendar.noPairingSelected', 'Bitte zuerst eine Paarung auswählen.'));
      return;
    }

    try {
      // ► Session sicherstellen
      await ensureMentoringSession();

      if (modalData.isEdit) {
        await axios.put(
          `/mentoring/calendar/${modalData.id}`,
          { type, title, start_time: start, end_time: end },
          { withCredentials: true }
        );
        log('Termin bearbeitet:', modalData.id, title);
      } else {
        await axios.post(
          '/mentoring/calendar',
          {
            title,
            start_time: start,
            end_time: end,
            matching_id: selectedMatchingId,
            type
          },
          { withCredentials: true }
        );
        log('Neuer Termin erstellt:', title);
      }
      await fetchEvents();
      setModalData(null);
    } catch (err) {
      setErrorMsg(t('calendar.saveError'));
      log('Fehler beim Speichern:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      // ► Session sicherstellen
      await ensureMentoringSession();

      await axios.delete(
        `/mentoring/calendar/${modalData.id}`,
        { withCredentials: true }
      );
      log('Termin gelöscht:', modalData.id);
      await fetchEvents();
      setModalData(null);
    } catch (err) {
      setErrorMsg(t('calendar.deleteError'));
      log('Fehler beim Löschen:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-[500px] bg-white text-black rounded-md overflow-hidden">
      {loading && (
        <div className="absolute z-50 top-0 left-0 w-full h-full bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg text-center text-lg">
            {t('calendar.loading')}
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="absolute z-50 top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-2 rounded">
          {errorMsg}
        </div>
      )}

      {/* Anzeige der lokalen Zeitzone + Info-Icon mit Tooltip */}
      <div className="px-4 pt-2 pb-1 text-xs text-gray-600 flex items-center gap-2">
        <span>
          {t('calendar.yourTimezone', {
            tz: localTimeZone,
            defaultValue: defaultTzLabel,
          })}
        </span>

        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-[10px] cursor-help"
          title={t('calendar.timezoneTooltip', { defaultValue: defaultTzTooltip })}
        >
          i
        </span>
      </div>

      <Calendar
        key={events.map(e => e.id).join(',')}
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        views={['month', 'week', 'day']}
        messages={{
          month: t('calendar.month'),
          week: t('calendar.week'),
          day: t('calendar.day'),
          today: t('calendar.today'),
          previous: t('calendar.previous'),
          next: t('calendar.next'),
        }}
        style={{ height: '100%' }}
      />

      {modalData && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-70 flex items-center justify-center z-50">
          <form
            onSubmit={handleSave}
            className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
          >
            <h3 className="text-lg font-bold mb-4">
              {modalData.isEdit ? t('calendar.editEvent') : t('calendar.newEvent')}
            </h3>

            <label className="block text-sm font-medium mb-1">
              {t('calendar.titleLabel')}
            </label>
            <input
              name="title"
              defaultValue={modalData.title || ''}
              required
              className="w-full border px-2 py-1 mb-3 rounded"
              disabled={loading}
              placeholder={t('calendar.titleLabel')}
            />

            {/* Hinweis zur Zeitzone im Dialog */}
            <p className="text-xs text-gray-500 mb-3">
              {t('calendar.timezoneNotice', {
                tz: localTimeZone,
                defaultValue: defaultTzNotice,
              })}
            </p>

            <label className="block text-sm font-medium mb-1">
              {t('calendar.startLabel')}
            </label>
            <input
              name="start"
              type="datetime-local"
              defaultValue={
                modalData.start ? format(modalData.start, "yyyy-MM-dd'T'HH:mm") : ''
              }
              required
              className="w-full border px-2 py-1 mb-3 rounded"
              disabled={loading}
            />

            <label className="block text-sm font-medium mb-1">
              {t('calendar.endLabel')}
            </label>
            <input
              name="end"
              type="datetime-local"
              defaultValue={
                modalData.end ? format(modalData.end, "yyyy-MM-dd'T'HH:mm") : ''
              }
              required
              className="w-full border px-2 py-1 mb-4 rounded"
              disabled={loading}
            />

            <label className="block text-sm font-medium mb-1">
              {t('calendar.typeLabel')}
            </label>
            <select
              name="type"
              defaultValue={modalData.type || ''}
              required
              className="w-full border px-2 py-1 mb-4 rounded"
              disabled={loading}
            >
              <option value="">{t('calendar.typeSelect')}</option>
              <option value="kennenlerntermin">{t('calendar.typeIntro')}</option>
              <option value="regulaer">{t('calendar.typeRegular')}</option>
              <option value="abschlusstermin">{t('calendar.typeEnd')}</option>
            </select>

            <div className="flex justify-between items-center">
              <div className="space-x-2">
                <button
                  type="submit"
                  className="bg-black text-white px-4 py-1 rounded"
                  disabled={loading}
                >
                  {t('calendar.save')}
                </button>
                {modalData.isEdit && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="bg-red-600 text-white px-4 py-1 rounded"
                    disabled={loading}
                  >
                    {t('calendar.delete')}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setModalData(null)}
                className="text-gray-500"
                disabled={loading}
              >
                {t('calendar.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default MenteeCalendar;
